import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { useTranslation } from "react-i18next";
import api from '../../api/client/axiosInstance';
import { toast } from "react-hot-toast";
import { FamilyTreeLoadingOverlay } from '../../components/common/skeletons';

// Data types
export type Person = {
  id: string;
  name: string;
  children?: Person[];
};

type FamilyMember = {
  name: string;
  age: number | null;
  gender: string | null;
  relation: string | null;
  isYou?: boolean;
  isElder?: boolean;
};

type FamilyData = {
  you: FamilyMember | null;
  parents?: FamilyMember[] | null;
  siblings?: FamilyMember[] | null;
  children?: FamilyMember[] | null;
  cousins?: FamilyMember[] | null;
  grandparents?: FamilyMember[] | null;
  unclesAunts?: FamilyMember[] | null;
  spouse?: FamilyMember | null;
  clients?: FamilyMember[] | null;
};


function sanitizeIdPart(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 40);
}

type GroupLabels = {
  you: string;
  parents: string;
  siblings: string;
  children: string;
  cousins: string;
  grandparents: string;
  unclesAunts: string;
  clients: string;
};

function convertFamilyDataToTree(data: FamilyData, labels: GroupLabels): Person {
  const youName = data?.you?.name || labels.you;
  const root: Person = {
    id: `you-${sanitizeIdPart(youName)}`,
    name: youName,
    children: [],
  };

  const addGroup = (label: string, list?: FamilyMember[] | null) => {
    if (!list || list.length === 0) return;
    root.children!.push({
      id: `grp-${sanitizeIdPart(label)}`,
      name: label,
      children: list.map((m, idx) => ({
        id: `${sanitizeIdPart(label)}-${idx}-${sanitizeIdPart(m.name || "member")}`,
        name: m.name || label,
      })),
    });
  };

  // Add spouse as its own node under root if present
  if (data?.spouse && data.spouse.name) {
    root.children!.push({
      id: `spouse-${sanitizeIdPart(data.spouse.name)}`,
      name: data.spouse.name,
    });
  }

  addGroup(labels.parents, data?.parents);
  addGroup(labels.siblings, data?.siblings);
  addGroup(labels.children, data?.children);
  addGroup(labels.cousins, data?.cousins);
  addGroup(labels.grandparents, data?.grandparents);
  addGroup(labels.unclesAunts, data?.unclesAunts);
  addGroup(labels.clients, data?.clients);

  return root;
}

const AVATAR_SILHOUETTE = (
  <path
    d="M12 13.5c3.59 0 6.5 2.24 6.5 5v.75a.75.75 0 0 1-.75.75H6.25a.75.75 0 0 1-.75-.75V18.5c0-2.76 2.91-5 6.5-5Zm0-1.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Z"
    fill="currentColor"
    opacity=".85"
  />
);

function linkPath(s: [number, number], t: [number, number]) {
  const x = d3.interpolateNumber(s[0], t[0])(0.5);
  return `M${s[0]},${s[1]} C ${s[0]},${(s[1] + t[1]) / 2} ${x},${(s[1] + t[1]) / 2} ${t[0]},${t[1]}`;
}

export default function FamilyTree() {
  const { t } = useTranslation(undefined, { keyPrefix: "familyTreePage" });
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);

  const groupLabels = useMemo(
    (): GroupLabels => ({
      you: t("you"),
      parents: t("parents"),
      siblings: t("siblings"),
      children: t("children"),
      cousins: t("cousins"),
      grandparents: t("grandparents"),
      unclesAunts: t("unclesAunts"),
      clients: t("clients"),
    }),
    [t]
  );

  const [rawFamilyData, setRawFamilyData] = useState<FamilyData | null>(null);

  const rootData = useMemo(() => {
    const fallback: FamilyData = {
      you: { name: groupLabels.you, age: null, gender: null, relation: groupLabels.you, isYou: true, isElder: false },
      parents: [],
      siblings: [],
      children: [],
      cousins: [],
      grandparents: [],
      unclesAunts: [],
      spouse: null,
      clients: [],
    };
    return convertFamilyDataToTree(rawFamilyData ?? fallback, groupLabels);
  }, [rawFamilyData, groupLabels]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const initialToastShownRef = useRef(false);

  const root = useMemo(() => d3.hierarchy<Person>(rootData), [rootData]);

  // Fetch family data from API
  const fetchFamilyData = async (isInitial: boolean = false) => {
    try {
      setLoading(true);
      console.log('Fetching family data from API...');
      
      const response = await api.get('/api/simple-invitations/family-relationships');
      console.log('Family data API response:', response.data);
      
      if (response.data.success) {
        // Check if the API response has the expected structure
        if (response.data.familyData) {
          setRawFamilyData(response.data.familyData);

          if (isInitial) {
            if (!initialToastShownRef.current) {
              toast.success(t("toastLoaded"));
              initialToastShownRef.current = true;
            }
          } else {
            toast.success(t("toastLoaded"));
          }
        } else {
          console.error('API response missing familyData:', response.data);
          toast.error(t("toastInvalidFormat"));
        }
      } else {
        console.error('API returned success: false', response.data);
        toast.error(response.data.message || t("toastFailedLoad"));
      }
    } catch (error: any) {
      console.error('Error fetching family data:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        toast.error(errorMessage);
        console.error('Server error details:', error.response.data);
      } else if (error.request) {
        // Network error
        toast.error(t("toastNetworkError"));
        console.error('Network error:', error.request);
      } else {
        // Other error
        toast.error(t("toastFailedLoad"));
        console.error('Other error:', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchFamilyData(true);
  }, []);

  // Layout computation
  const layout = useMemo(() => {
    const tree = d3
      .tree<Person>()
      // Horizontal spacing equals card width (168px) + 5px gap
      .nodeSize([173, 140])
      .separation((a, b) => (a.parent === b.parent ? 1.2 : 1.6));

    const copy = root.copy();
    copy.eachBefore((d) => {
      if (collapsed.has(d.data.id)) d.children = null;
    });

    return tree(copy);
  }, [root, collapsed]);

  // Collapse all nodes on first render
  useEffect(() => {
    if (collapsed.size > 0) return; // already initialized
    const next = new Set<string>();
    root.each((d) => {
      if (d.children && d.children.length > 0) {
        next.add(d.data.id);
      }
    });
    setCollapsed(next);
  }, [root]);

  // Zoom & pan functionality
  useEffect(() => {
    if (!svgRef.current || !gRef.current) return;
    const svg = d3.select(svgRef.current);
    const g = d3.select(gRef.current);

    const zoomed = (event: any) => g.attr("transform", event.transform.toString());
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      // Allow much smaller zoom-out to see the full tree
      .scaleExtent([0.05, 3])
      .on("zoom", zoomed);
    svg.call(zoom as any);

    const { width, height } = svgRef.current.getBoundingClientRect();
    const xExtent = d3.extent(layout.descendants(), (d) => d.x) as [number, number];
    const yExtent = d3.extent(layout.descendants(), (d) => d.y) as [number, number];
    const contentWidth = xExtent[1] - xExtent[0] + 240;
    const contentHeight = yExtent[1] - yExtent[0] + 240;
    const scale = Math.min(width / contentWidth, height / contentHeight);
    const transform = d3.zoomIdentity
      .translate(width / 2, 60)
      .scale(Math.max(0.3, Math.min(1.2, scale)))
      .translate(-layout.x, 0);
    svg.call(zoom.transform as any, transform);

    return () => {
      svg.on("wheel.zoom", null);
    };
  }, [layout]);

  // Focus on selected node
  const focusOn = (nodeId: string) => {
    const node = layout.descendants().find((d) => d.data.id === nodeId);
    if (!node || !svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const { width, height } = svgRef.current.getBoundingClientRect();
    const transform = d3.zoomIdentity
      .translate(width / 2, height * 0.18)
      .scale(1.1)
      .translate(-node.x, -node.y);
    svg.transition().duration(600).call((d3 as any).zoom().transform, transform);
  };

  // Toggle node collapse
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  // Collapse all descendants of a node
  const collapseAll = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      const node = layout.descendants().find((d) => d.data.id === id);
      if (node) {
        // Collapse the entire subtree
        node.descendants().forEach((d) => next.add(d.data.id));
      } else {
        next.add(id);
      }
      return next;
    });

  // Expand all descendants of a node
  const expandAll = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      const node = layout.descendants().find((d) => d.data.id === id);
      if (node) {
        // Remove collapsed marks for this node and all its descendants
        node.descendants().forEach((d) => next.delete(d.data.id));
      } else {
        next.delete(id);
      }
      return next;
    });

  const nodes = layout.descendants();
  const links = layout.links();

  return (
    <div
      ref={containerRef}
      className="family-tree-scope fixed top-16 left-0 lg:left-64 right-0 bottom-0 bg-[#ffffff] dark:bg-slate-950 transition-colors duration-200"
      style={{ zIndex: 10 }}
    >
      {loading && <FamilyTreeLoadingOverlay />}
      <div className="absolute inset-0 bg-white dark:bg-slate-900 overflow-hidden transition-colors duration-200">
        <svg ref={svgRef} className="w-full h-full block select-none" aria-label={t("ariaFamilyTree")}>
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.15" />
            </filter>
          </defs>
          <g ref={gRef}>
            {links.map((l, i) => (
              <path
                key={i}
                d={linkPath([l.source.x, l.source.y + 36], [l.target.x, l.target.y - 48])}
                stroke="#B8C2CC"
                strokeWidth={2}
                fill="none"
                opacity={0.9}
              />
            ))}

            {nodes.map((n) => (
              <g key={n.data.id} transform={`translate(${n.x},${n.y})`}>
                <rect
                  x={-84}
                  y={-36}
                  width={168}
                  height={100}
                  rx={10}
                  fill={selected === n.data.id ? "#DBEAFE" : "#ffffff"}
                  stroke={selected === n.data.id ? "#2B79C2" : "#E5E7EB"}
                  strokeWidth={selected === n.data.id ? 2 : 1}
                  filter="url(#shadow)"
                />
                <text x={0} y={-14} textAnchor="middle" fontSize={12} fontWeight={700} fill="#6B7280" style={{ userSelect: "none" }}>
                  {n.data.name.toUpperCase()}
                </text>

                <g className="cursor-pointer" onClick={() => {
                  setSelected(n.data.id);
                  focusOn(n.data.id);
                }}>
                  <circle cx={0} cy={26} r={28} fill="#fff" stroke="#2B79C2" strokeWidth={4} />
                  <g transform="translate(-12,12)" fill="#111827">
                    {AVATAR_SILHOUETTE}
                  </g>
                </g>

                {n.data.children && n.data.children.length > 0 && (
                  <g transform={`translate(${0},${68})`}>
                    {collapsed.has(n.data.id) ? (
                      <g className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggle(n.data.id); }}>
                        <circle cx={0} cy={0} r={10} fill="#2B79C2" />
                        <text x={0} y={4} textAnchor="middle" fontSize={14} fontWeight={700} fill="#fff">+</text>
                      </g>
                    ) : (
                      <g className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggle(n.data.id); }}>
                        <circle cx={0} cy={0} r={10} fill="#EF4444" />
                        <text x={0} y={4} textAnchor="middle" fontSize={16} fontWeight={800} fill="#fff">–</text>
                      </g>
                    )}
                  </g>
                )}
              </g>
            ))}
          </g>
        </svg>
      </div>

      {selected && (
        <aside className="family-tree-panel fixed right-4 top-16 bottom-4 w-80 rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-gray-200 dark:ring-slate-700 shadow-lg p-4 flex flex-col z-50 transition-colors duration-200">
          <div className="flex items-center gap-3 border-b border-gray-200 dark:border-slate-700 pb-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-800 grid place-items-center text-gray-600 dark:text-slate-300">
              <svg viewBox="0 0 24 24" width="20" height="20">{AVATAR_SILHOUETTE}</svg>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 dark:text-slate-400">{t("selected")}</div>
              <div className="font-semibold text-gray-800 dark:text-slate-100 text-base break-words max-w-[220px]">
                {nodes.find((n) => n.data.id === selected)?.data.name || t("none")}
              </div>
            </div>
            <button className="ml-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300" onClick={() => setSelected(null)} aria-label={t("closeAria")}>✕</button>
          </div>

          <div className="mt-4 grid gap-2">
            <Action label={t("focusTreeOnSelected")} onClick={() => selected && focusOn(selected)} />
            <Action label={t("expandOneLevel")} onClick={() => selected && toggle(selected)} />
            <Action label={t("expandAllUnder")} onClick={() => selected && expandAll(selected)} />
            <Action label={t("collapseSelected")} onClick={() => selected && collapseAll(selected)} />
            <Action label={t("refreshFamilyData")} onClick={fetchFamilyData} />
          </div>

          <div className="mt-auto pt-4 text-xs text-gray-400 dark:text-slate-500">
            {t("zoomHint")}
          </div>
        </aside>
      )}
    </div>
  );
}

function Action({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="family-tree-action flex items-center justify-between w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/60 dark:hover:bg-blue-950/40 text-gray-700 dark:text-slate-200"
    >
      <span className="text-sm font-medium">{label}</span>
      <svg viewBox="0 0 20 20" width="18" height="18" className="text-blue-600" aria-hidden>
        <path d="M7 5l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    </button>
  );
}
